import { useCallback, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { PauseCircle, Printer as PrinterIcon, X } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useOrdersSignalR } from '@/features/orders/hooks/useOrdersSignalR';
import { useTrayNotices, type TrayNotice } from '@/shared/components/NoticeTray';
import { pointsToCurrentRoute } from '@/shared/lib/current-route';
import { usePrintingStatus } from '../hooks/usePrinting';
import { usePrintingAvailable } from '../hooks/usePrintingAvailable';
import { printingNotice } from '../lib/printing-notice';
import {
  addOverdue,
  claimOverdueAlert,
  describeOverdueOrder,
  overdueAlertKey,
  overdueLinesSuppressed,
  resolveOverdue,
  type OverdueOrder,
} from '../lib/overdue-alerts';
import { playPrintingAlert } from '../lib/printing-alert-sound';
import type { PrintJobUpdatedEvent } from '../types/printing.types';
import styles from './PrintingStatusBar.module.css';

/**
 * Faixa fixa, aviso de pausa e aviso sonoro da impressão, em qualquer página
 * do painel, para quem vê pedidos. Some sozinho quando a loja está fora do
 * piloto: nenhuma leitura, nenhuma conexão.
 */
export function PrintingStatusBar() {
  const { available } = usePrintingAvailable('orders.read');
  if (!available) return null;
  return <PrintingStatusBarContent />;
}

function PrintingStatusBarContent() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const can = {
    settingsWrite: permissions?.includes('settings.write') ?? false,
    billingRead: permissions?.includes('billing.read') ?? false,
  };
  const location = useLocation();

  // Revalidado a cada 60 s, na reconexão e nos eventos `printing.*`, que a
  // conexão compartilhada de pedidos já trata como campainha.
  const { data: status } = usePrintingStatus();
  const [overdue, setOverdue] = useState<readonly OverdueOrder[]>([]);
  // A lista também num ref: o evento decide se o pedido é novo (e toca) a
  // partir da lista atual, sem esperar o próximo render.
  const overdueRef = useRef<readonly OverdueOrder[]>([]);
  const update = useCallback((next: readonly OverdueOrder[]) => {
    overdueRef.current = next;
    setOverdue(next);
  }, []);

  const handleJobUpdated = useCallback(
    (event: PrintJobUpdatedEvent) => {
      // O cupom que sai depois do atraso tira a linha dele.
      let next = resolveOverdue(overdueRef.current, event);
      const key = overdueAlertKey(event);
      if (key && event.orderId && claimOverdueAlert(key)) {
        const added = addOverdue(next, event, key);
        next = added.orders;
        // Uma vez por pedido: os cupons do mesmo pedido atrasam juntos, e
        // três bipes seguidos para um problema só não ajudam. O som toca
        // também com o computador offline, quando a linha fica de fora.
        if (added.newOrder) playPrintingAlert();
      }
      if (next !== overdueRef.current) update(next);
    },
    [update],
  );

  useOrdersSignalR({ onPrintingJobUpdated: handleJobUpdated });

  const notice = status ? printingNotice(status, can) : null;
  // Com o computador offline ou a impressão pausada, suspensa ou desligada, a faixa ou o
  // aviso de pausa já dizem a causa (P1-11).
  const shownOverdue = overdueLinesSuppressed(status) ? [] : overdue;
  const dismiss = (orderId: string) => update(overdueRef.current.filter((order) => order.orderId !== orderId));
  const showLink = (to: string) => !pointsToCurrentRoute(to, location);

  const trayNotices: TrayNotice[] = [];
  if (notice?.kind === 'banner') trayNotices.push({ key: 'printing-banner', text: notice.summary, level: notice.level });
  if (notice?.kind === 'paused') trayNotices.push({ key: 'printing-paused', text: notice.text, level: 'info' });
  for (const order of shownOverdue) {
    trayNotices.push({
      key: `printing-overdue:${order.orderId}:${order.tickets.length}`,
      text: describeOverdueOrder(order),
      level: 'normal',
    });
  }
  useTrayNotices(trayNotices);

  return (
    <>
      {notice?.kind === 'banner' && (
        // Não dispensável: some sozinha quando o problema acaba.
        <div className={styles.banner} role="alert" data-testid="printing-banner">
          <PrinterIcon size={16} className={styles.icon} aria-hidden="true" />
          <div className={styles.lines}>
            {notice.lines.map((line) => (
              <span key={line} className={styles.text}>
                {line}
              </span>
            ))}
          </div>
          {notice.link && showLink(notice.link.to) && (
            <Link to={notice.link.to} className={styles.link}>
              {notice.link.label}
            </Link>
          )}
        </div>
      )}

      {notice?.kind === 'paused' && (
        <div className={styles.paused} role="status" data-testid="printing-paused">
          <PauseCircle size={13} className={styles.icon} aria-hidden="true" />
          <span>{notice.text}</span>
          {notice.link && showLink(notice.link.to) && (
            <Link to={notice.link.to} className={styles.pausedLink}>
              {notice.link.label}
            </Link>
          )}
        </div>
      )}

      {shownOverdue.length > 0 && (
        <ul className={styles.alerts} aria-label="Avisos de impressão">
          {shownOverdue.map((order) => (
            <li key={order.orderId} className={styles.alert} role="alert" data-testid="printing-overdue">
              <span className={styles.alertText}>{describeOverdueOrder(order)}</span>
              <Link
                to={`/orders?pedido=${encodeURIComponent(order.orderId)}`}
                className={styles.link}
                onClick={() => dismiss(order.orderId)}
              >
                Ver pedido
              </Link>
              <button
                type="button"
                className={styles.dismiss}
                onClick={() => dismiss(order.orderId)}
                aria-label="Fechar aviso"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
